import { FALLBACK_REGISTRATION_OPTIONS } from './pendentesConstants';
import {
  cleanDigits,
  isExternalPortalReference,
  moneyInputValue,
  parseCurrencyInput,
  propertyTitle,
  sanitizeExternalPortalReferences,
} from './pendentesFormatters';

export function defaultRegistrationOptions() {
  return Object.entries(FALLBACK_REGISTRATION_OPTIONS).reduce(
    (options, [key, value]) => ({ ...options, [key]: [...value] }),
    {}
  );
}

export function mediaDisclosureForItem(item = {}) {
  const sourceText =
    `${item.sourcePortal || ''} ${item.sourceUrl || ''}`.toLowerCase();
  if (sourceText.includes('olx')) return { value: '63', label: 'Portal OLX' };
  if (sourceText.includes('vivareal') || sourceText.includes('viva real')) {
    return { value: '15', label: 'Portal Viva Real' };
  }
  if (sourceText.includes('zap')) return { value: '13', label: 'Portal Zap' };
  if (sourceText.includes('imovelweb')) {
    return { value: '14', label: 'Portal Imovelweb' };
  }
  return { value: '30', label: 'Internet' };
}

export function propertyTypeForItem(value) {
  const normalized = String(value || '').toLowerCase();
  if (normalized.includes('cobertura'))
    return { value: '2', label: 'Cobertura' };
  if (normalized.includes('kitnet') || normalized.includes('conjugado')) {
    return { value: '4', label: 'Kitnet/Conjugado' };
  }
  if (normalized.includes('flat')) return { value: '25', label: 'Flat' };
  if (normalized.includes('loft')) return { value: '5', label: 'Loft' };
  if (normalized.includes('studio')) return { value: '34', label: 'Studio' };
  if (normalized.includes('casa')) return { value: '3', label: 'Casa de rua' };
  return { value: '1', label: 'Apartamento' };
}

export function isValidImageUrl(value) {
  const textValue = String(value || '').trim();
  if (/^data:image\/jpe?g;base64,/i.test(textValue)) return true;
  try {
    const parsed = new URL(textValue);
    return (
      ['http:', 'https:'].includes(parsed.protocol) &&
      /\.(jpe?g)$/i.test(parsed.pathname)
    );
  } catch {
    return false;
  }
}

export function normalizeMediaImages(images = []) {
  const seen = new Set();
  return (Array.isArray(images) ? images : [])
    .map((image, index) => {
      const item =
        image && typeof image === 'object' ? image : { url: String(image) };
      const url = String(
        item.url || item.dataUrl || item.data_url || ''
      ).trim();
      const isUpload = /^data:image\/jpe?g;base64,/i.test(url);
      if (!url || !isValidImageUrl(url) || seen.has(url)) {
        return null;
      }
      seen.add(url);
      return {
        id: String(item.id || `img-${index + 1}`),
        url,
        dataUrl: String(item.dataUrl || item.data_url || (isUpload ? url : '')),
        caption: String(item.caption || item.label || '').trim(),
        source: isUpload ? 'upload' : String(item.source || 'galeria').trim(),
        name: String(
          item.name ||
            item.filename ||
            (isUpload ? `foto-${index + 1}.jpg` : url.split('/').pop()) ||
            `foto-${index + 1}.jpg`
        ),
        filename: String(item.filename || item.name || `foto-${index + 1}.jpg`),
        mimeType: String(item.mimeType || item.mime_type || 'image/jpeg'),
        size: Number(item.size || 0),
        position: index + 1,
      };
    })
    .filter(Boolean);
}

export function defaultRegistrationFields(item = {}) {
  const price = moneyInputValue(item.competitorPrice);
  const media = mediaDisclosureForItem(item);
  const propertyType = propertyTypeForItem(item.propertyType);
  const usefulArea = item.area || item.usefulArea || '';
  return {
    objective: 'compra',
    ownerName: item.ownerName || '',
    ownerPhone: item.ownerPhone || '',
    ownerEmail: '',
    mediaDisclosureId: media.value,
    mediaDisclosureLabel: media.label,
    contactFormId: item.ownerPhone ? '10' : '7',
    contactFormLabel: item.ownerPhone ? 'WhatsApp' : 'Portais',
    saleEnabled: true,
    saleStatusId: '1',
    saleStatusLabel: 'AGUARDANDO LIBERAÇÃO VENDA',
    salePrice: price,
    rentEnabled: false,
    rentStatusId: '13',
    rentStatusLabel: 'AGUARDANDO LIBERAÇÃO LOCAÇÃO',
    rentPrice: '',
    propertyTypeId: propertyType.value,
    propertyTypeLabel: propertyType.label || item.propertyType || '',
    laudoClassId: '1',
    optionTypeId: '1',
    optionTypeLabel: 'Laudo sem documento',
    postalCode: '',
    address: item.address || '',
    streetNumber: item.streetNumber || '',
    complement: item.complement || '',
    complementTypeId: '',
    complementTypeLabel: '',
    complementText: item.complement || '',
    complement2TypeId: '',
    complement2TypeLabel: '',
    complement2Text: '',
    neighbourhood: item.neighbourhood || '',
    city: item.city || '',
    bedrooms: item.bedrooms ?? '',
    bathrooms: item.bathrooms ?? '',
    parkingSpaces: item.parkingSpaces ?? '',
    livingRooms: 1,
    suites: item.suites ?? '',
    usefulArea,
    totalArea: usefulArea,
    landArea: '',
    externalArea: '',
    condominiumFee: '',
    iptuValue: '',
    furnished: false,
    adTitle: propertyTitle(item),
    googleTitle: '',
    propertyDescription: '',
    slideDescription: '',
    amenitiesText: '',
    internalNotes: '',
    sensitiveNotes: sanitizeExternalPortalReferences(item.messageText || ''),
    sourceUrl: item.sourceUrl || '',
    sourcePortal: item.sourcePortal || item.sourceKind || '',
    mediaImages: [],
  };
}

export function sanitizeRegistrationFields(fields = {}) {
  const next = { ...fields };
  [
    'adTitle',
    'googleTitle',
    'amenitiesText',
    'internalNotes',
    'sensitiveNotes',
  ].forEach(key => {
    next[key] = sanitizeExternalPortalReferences(next[key]);
  });
  if (isExternalPortalReference(next.sourceUrl)) next.sourceUrl = '';
  if (isExternalPortalReference(next.sourcePortal)) {
    next.sourcePortal = 'Portal externo';
  }
  next.mediaImages = normalizeMediaImages(next.mediaImages || []);
  return next;
}

export function reviewRegistrationFields(fields = {}, item = {}) {
  const next = sanitizeRegistrationFields(fields);
  next.sourceUrl = fields.sourceUrl || item.sourceUrl || '';
  next.sourcePortal =
    fields.sourcePortal || item.sourcePortal || item.sourceKind || '';
  return next;
}

export function normalizeRegistrationOptions(options = {}) {
  const fallback = defaultRegistrationOptions();
  return Object.keys(fallback).reduce((normalized, key) => {
    const value = Array.isArray(options[key]) ? options[key] : [];
    return {
      ...normalized,
      [key]: value.length ? value : fallback[key],
    };
  }, {});
}

export function validateRegistrationFields(fields) {
  const errors = [];
  if (!String(fields.ownerName || '').trim()) {
    errors.push('Informe o nome do proprietário.');
  }
  if (cleanDigits(fields.postalCode).length !== 8) {
    errors.push('Informe um CEP válido.');
  }
  if (!String(fields.address || '').trim()) {
    errors.push('Informe o logradouro.');
  }
  if (!String(fields.streetNumber || '').trim()) {
    errors.push('Informe o número.');
  }
  if (parseCurrencyInput(fields.salePrice) <= 0) {
    errors.push('Informe o valor de venda.');
  }
  if (!String(fields.propertyTypeId || '').trim()) {
    errors.push('Selecione o tipo do imóvel.');
  }
  if (!String(fields.mediaDisclosureId || '').trim()) {
    errors.push('Selecione a mídia de divulgação.');
  }
  const hasPhone = cleanDigits(fields.ownerPhone).length >= 10;
  const email = String(fields.ownerEmail || '').trim();
  const hasEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  if (!hasPhone && !hasEmail) {
    errors.push('Informe telefone ou e-mail do proprietário.');
  }
  if (email && !hasEmail) {
    errors.push('Informe um e-mail válido.');
  }
  return errors;
}

export function registrationStepKeyForFieldError(error) {
  const text = String(error || '').toLowerCase();
  if (
    text.includes('proprietário') ||
    text.includes('telefone') ||
    text.includes('e-mail') ||
    text.includes('mídia')
  ) {
    return 'identity';
  }
  if (
    text.includes('cep') ||
    text.includes('logradouro') ||
    text.includes('número') ||
    text.includes('tipo do imóvel')
  ) {
    return 'property';
  }
  if (text.includes('valor')) return 'pricing';
  return 'publication';
}
